import { useRef, type CSSProperties, type ReactNode } from 'react';
import { Link, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Building2, Inbox, ListChecks, PanelLeftOpen, Plus, Search } from 'lucide-react';
import { MOD, Tip } from '@vatman/ui';
import type { Session } from '../lib/api';
import { useSession } from '../lib/session';
import { orgContext } from '../lib/spaces';
import { WorkspaceNav, type NavActive } from './WorkspaceNav';
import { openQuickOpen, QuickOpen } from './QuickOpen';
import { CreateMenu } from './CreateMenu';
import { UserMenu } from './dashboard/Dashboard';
import { useUnreadCount } from '../notifications/notifications';
import { NAV_WIDTH, setNavDrawer, setNavHidden, setNavWidth, useNav, useNavKeys } from './navState';
import '../shell.css';

/**
 * The frame of the pages with the menu (main.tsx): the menu, ⌘K and the drawer's backdrop, made
 * once, so going from page to page keeps the menu as it was (open branches, scroll). A page renders
 * its own `<main className="main">` and tells the menu where it is (useNavPlace). Signed out or
 * still loading, the page alone decides what to show.
 */
export function AppShell() {
  const session = useSession();
  if (!session.data?.user) return <Outlet />;
  return <Shell session={session.data} />;
}

function Shell({ session }: { session: Session }) {
  const { org, active, hidden, width, drawer } = useNav();
  useNavKeys(true);
  return (
    <div className="dash" data-nav={hidden ? 'hidden' : 'shown'} style={{ '--nav-w': `${width}px` } as CSSProperties}>
      <QuickOpen />
      {hidden && <NavRail session={session} org={org === undefined ? orgContext(session.orgs) : org} active={active} />}
      <WorkspaceNav session={session} org={org === undefined ? orgContext(session.orgs) : org} active={active} />
      <Outlet />
      {/* After the page, so it is drawn over the page's edge without a z-index. */}
      {!hidden && <ResizeEdge />}
      {drawer && <div className="nav-backdrop" onClick={() => setNavDrawer(false)} />}
    </div>
  );
}

/**
 * The strip left of a hidden menu: the button that brings the menu back, and what one reaches for
 * most — "Create", search, Inbox, My issues, everything in the organization, and one's own menu.
 */
function NavRail({ session, org, active }: { session: Session; org: string | null; active: NavActive }) {
  const { t } = useTranslation();
  const unread = useUnreadCount().data ?? 0;
  const link = (to: string, on: boolean, label: string, icon: ReactNode, badge?: number) => (
    <Tip label={label} side="right">
      <Link to={to} className="icon-btn" data-active={on} aria-label={label}>
        {icon}
        {!!badge && <span className="count inbox-count nav-rail-badge">{badge > 99 ? '99+' : badge}</span>}
      </Link>
    </Tip>
  );
  return (
    <div className="nav-rail">
      <Tip label={t('nav.show')} keys={[MOD, '\\']} side="right">
        <button className="icon-btn" aria-label={t('nav.show')} onClick={() => setNavHidden(false)}>
          <PanelLeftOpen size={18} />
        </button>
      </Tip>
      <CreateMenu>
        <button className="icon-btn nav-rail-create" aria-label={t('create.title')} title={t('create.title')}>
          <Plus size={18} />
        </button>
      </CreateMenu>
      <Tip label={t('quickOpen.button')} keys={[MOD, 'K']} side="right">
        <button className="icon-btn" aria-label={t('quickOpen.button')} onClick={() => openQuickOpen()}>
          <Search size={18} />
        </button>
      </Tip>
      <span className="nav-rail-sep" />
      {link('/inbox', active.kind === 'inbox', t('inbox.title'), <Inbox size={18} />, unread)}
      {link('/t', active.kind === 'myIssues', t('tracker.myIssues'), <ListChecks size={18} />)}
      {link(org ? `/?org=${org}` : '/', active.kind === 'view', t(org ? 'nav.everything' : 'nav.everythingPersonal'), <Building2 size={18} />)}
      <span className="nav-rail-gap" />
      <UserMenu session={session} compact />
    </div>
  );
}

/** The menu's right edge: drag to change the width, double-click for the usual one. */
function ResizeEdge() {
  const { t } = useTranslation();
  const start = useRef<{ x: number; width: number } | null>(null);
  // Kept once the drag ends, however it ends (released, or the capture lost).
  const finish = () => {
    if (!start.current) return;
    start.current = null;
    setNavWidth(useNav.getState().width);
  };
  return (
    <div
      className="nav-resize"
      role="separator"
      aria-orientation="vertical"
      aria-label={t('nav.resize')}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { x: e.clientX, width: useNav.getState().width };
      }}
      onPointerMove={(e) => {
        if (start.current) setNavWidth(start.current.width + e.clientX - start.current.x, false);
      }}
      onPointerUp={finish}
      onLostPointerCapture={finish}
      onDoubleClick={() => setNavWidth(NAV_WIDTH.normal)}
    />
  );
}
